import { Component, EventEmitter, HostListener, Input, OnInit, Output } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { VerificationDataRequirements } from 'ng-voxloud';
import { forkJoin, Observable, Subscription } from 'rxjs';
import { concatMap } from 'rxjs/operators';

import {
  AddDidRequest,
  DidProvider,
  DID_STATUS,
  DID,
  DidByCompanyIdResponse,
  VerificationData,
  DID_CLASS
} from 'voxloud-types/dist/models';
import { AddDidManuallyRequest, PortabilityRequestResponse } from 'src/app/domains/dids/models';
import { DidsService, VerificationDataService } from 'src/app/domains/dids/services';
import { PortabilityService } from 'src/app/domains/dids/services/portability.service';
import { EstimationRequest, PRODUCT_TYPE } from 'src/app/domains/payment/models/payment.model';
import { OPTION_TYPE } from 'src/app/shared/components';
import { Subscription as SubscriptionModel } from 'src/app/domains/subscription/models';

enum STEP {
  SELECT_TYPE = 'select-type',
  DID_DATA = 'did-data',
  ADD_VD = 'add-vd',
  ADD_PORTBILITY_DOCS = 'add-portability-docs',
  RECAP = 'recap'
};

enum SELECTION_TYPE {
  VD = 'vd',
  PORTABILITY = 'portability'
};

const VD_STATUSES = [DID_STATUS.WAITING_VERIFICATION_DATA, DID_STATUS.WAITING_PROVIDER_VALIDATION];
const PORTABILITY_STATUSES = [DID_STATUS.ONGOING_PORTABILITY, DID_STATUS.ACTIVE];

@Component({
  selector: 'gdp-add-port-did-popup',
  templateUrl: './add-port-did-popup.component.html',
  styleUrls: ['./add-port-did-popup.component.scss']
})
export class AddPortDidPopupComponent implements OnInit {
  @Input() companyId: number;
  public product: EstimationRequest;
  public currentStep: STEP;
  public selectedType: SELECTION_TYPE;
  public STEPS = STEP;
  public SELECTION_TYPE = SELECTION_TYPE;
  public subscription: SubscriptionModel;
  public language: string;
  public loading = {
    getInitData: false,
    getDids: false,
    addDid: false
  };
  public statuses: DID_STATUS[] = [];
  public selectedStatus: DID_STATUS;
  public providers: string[] = [];
  public requirements: VerificationDataRequirements;
  public isVDRequired: boolean = false;
  public areDocsRequired: boolean = false;
  public country: string = 'it';
  public didData = {
    value: null,
    isValid: false
  };
  public checkboxesData = {
    value: null,
    isValid: false
  };
  public verificationData = {
    type: '',
    value: null,
    isValid: false,
    selectedOpt: null
  };
  public portabilityDocsForm = {
    files: null,
    isValid: false
  };
  public didsWithVerificationData: DID[] = [];
  public showDidError: boolean = false;
  public isVerticalVDTable: boolean = true;
  private languageSubscription: Subscription;
  public estimateError: boolean = false;
  public planAddonHandling: string;
  @Output() onAddExistingOrPortedDid: EventEmitter<string> = new EventEmitter();

  constructor(
    private activeModal: NgbActiveModal,
    private verificationDataService: VerificationDataService,
    private didsService: DidsService,
    private portabiltyService: PortabilityService,
    private translate: TranslateService
  ) { }

  @HostListener('window:resize', ['$event'])
  onResize(event) {
    this.isVerticalVDTable = window.innerWidth <= 991;
  }

  ngOnInit(): void {
    this.currentStep = STEP.SELECT_TYPE;
    this.selectedType = SELECTION_TYPE.VD;
    this.statuses = VD_STATUSES;
    this.selectedStatus = this.statuses[0];
    this.languageSubscription = this.translate.onLangChange.subscribe((l: LangChangeEvent) => {
      this.language = l.lang;
    });
  }

  public onSelectRadio(value: SELECTION_TYPE): void {
    this.selectedType = value;
    switch (this.selectedType) {
      case SELECTION_TYPE.VD: {
        this.statuses = VD_STATUSES;
        this.areDocsRequired = false;
      }
        break;
      case SELECTION_TYPE.PORTABILITY: {
        this.statuses = PORTABILITY_STATUSES;
        this.isVDRequired = false;
      }
        break;
      default: break;
    }
    this.selectedStatus = this.statuses[0];
  }

  public isDisabled(): boolean {
    let result: boolean;
    switch (this.currentStep) {
      case STEP.DID_DATA: {
        if (
          (this.selectedType === SELECTION_TYPE.VD && !this.isVDRequired)
          || (this.selectedType === SELECTION_TYPE.PORTABILITY && !this.areDocsRequired)
        ) {
          result = !this.didData.isValid;
        } else {
          result = !this.didData.isValid;
        }
      }
        break;
      case STEP.ADD_VD: {
        result = !this.verificationData.isValid;
      }
        break;
      case STEP.ADD_PORTBILITY_DOCS: {
        result = !this.portabilityDocsForm.isValid;
      }
        break;
      default: break;
    }
    return result;
  }

  public onGoNext(step): void {
    if (step === this.STEPS.RECAP) {
      this.product = {
        product: this.didData.value.class === DID_CLASS.VOICE ? PRODUCT_TYPE.phone_number : PRODUCT_TYPE.fax,
        default_addon: this.didData.value.class === DID_CLASS.VOICE ? PRODUCT_TYPE.phone_number : PRODUCT_TYPE.fax,
        country_code: this.country.toLowerCase(),
        did_type: this.didData.value.type,
        coupon_id: this.didData.value.couponId ? this.didData.value.couponId : null
      };
    }
    this.currentStep = step;
  }

  public onGoBack(): void {
    if (this.currentStep === this.STEPS.DID_DATA) {
      this.currentStep = this.STEPS.SELECT_TYPE;
    }
    if (this.currentStep === this.STEPS.ADD_VD) {
      this.currentStep = this.STEPS.DID_DATA;
    }
    if (this.currentStep === this.STEPS.ADD_PORTBILITY_DOCS) {
      this.currentStep = this.STEPS.DID_DATA;
    }
    if (this.currentStep === this.STEPS.RECAP) {
      if (this.selectedType === this.SELECTION_TYPE.PORTABILITY) {
        if (this.selectedStatus === this.statuses[1]) {
          this.currentStep = this.STEPS.DID_DATA;
        }
        else this.currentStep = this.STEPS.ADD_PORTBILITY_DOCS;
      }
      if (this.selectedType === this.SELECTION_TYPE.VD) {
        if (this.selectedStatus === this.statuses[0]) {
          this.currentStep = this.STEPS.DID_DATA;
        }
        else this.currentStep = this.STEPS.ADD_VD;
      }
    }
    this.checkboxesData.isValid = false;
  }

  public onSubmitSelection(): void {
    this.currentStep = STEP.DID_DATA;
    if (this.selectedType === 'vd') {
      switch (this.selectedStatus) {
        case DID_STATUS.WAITING_VERIFICATION_DATA: {
          this.isVDRequired = false;
          this.getProviders();
        }
          break;
        default: {
          this.getInitData();
        }
      }
    } else {
      if (this.selectedStatus === DID_STATUS.ACTIVE) { this.areDocsRequired = false; }
      else { this.areDocsRequired = true; }
      this.getProviders();
    }
  }

  public getVDRequirements(country: string): void {
    if (this.country !== country) {
      this.country = country;
      if (this.selectedType === SELECTION_TYPE.VD) {
        this.loading.getInitData = true;
        this.verificationDataService.getVerificationDataRequirementsByCountry(this.country)
          .subscribe((res: VerificationDataRequirements) => {
            this.requirements = res;
            this.isVDRequired = !!(res.BUSINESS || res.PRIVATE);
            this.loading.getInitData = false;
          });
      }
    }
  }

  public onSubmitDidData(): void {
    if (this.isVDRequired) {
      this.currentStep = STEP.ADD_VD;
      this.getDids();
    } else if (this.areDocsRequired) {
      this.currentStep = STEP.ADD_PORTBILITY_DOCS;
    } else {
      if(
        (this.selectedStatus === DID_STATUS.WAITING_VERIFICATION_DATA
        || this.selectedStatus === DID_STATUS.ACTIVE)
        && this.planAddonHandling === 'manual'
      ) {
        this.onSubmit();
      } else {
        this.onGoNext(STEP.RECAP);
      }
    }
  }

  public onSubmit() {
    if (!this.isVDRequired && !this.areDocsRequired ) {
      const payload: AddDidRequest | AddDidManuallyRequest = this.buildAddDidPayload();
      this.addExistingOrPortedDid(payload);
    }

    if (this.selectedType === SELECTION_TYPE.PORTABILITY && this.selectedStatus !== DID_STATUS.ACTIVE) {
      this.onSubmitPortabilityDocs();
    }

    if (this.selectedType === SELECTION_TYPE.VD && this.isVDRequired) {
      this.onAddDidWithVD();
    }
  }

  // Helper methods
  private getInitData(): void {
    this.loading.getInitData = true;
    forkJoin({
      p: this.didsService.getProviders(),
      r: this.verificationDataService.getVerificationDataRequirementsByCountry(this.country)
    }).subscribe((res: { p: DidProvider[], r: VerificationDataRequirements }) => {
      this.providers = res.p.map(provider => provider.name);
      this.requirements = res.r;
      this.isVDRequired = !!(res.r.BUSINESS || res.r.PRIVATE);
      this.loading.getInitData = false;
    })
  }

  private getProviders(): void {
    this.loading.getInitData = true;
    this.didsService.getProviders().subscribe((res: DidProvider[]) => {
      this.providers = res.map(provider => provider.name);
      this.loading.getInitData = false;
    });
  }

  private getDids(): void {
    this.loading.getDids = true;
    this.didsService.getDidsByCompanyId(this.companyId).subscribe((res: DidByCompanyIdResponse[]) => {
      this.didsWithVerificationData = res[0].dids.filter((el: DID) => el.hasOwnProperty('verification_data_id'));
      this.showDidError = false;
      this.loading.getDids = false;
    }, () => {
      this.showDidError = true;
      this.loading.getDids = false;
    });
  }

  // submit when verification data are required
  private onAddDidWithVD(): void {
    if (this.verificationData.selectedOpt.type === OPTION_TYPE.CLONE) {
      // if verification data already exists
      const payload: AddDidRequest | AddDidManuallyRequest = {
        ...this.buildAddDidPayload(),
        verification_data_id: this.verificationData.value.id
      };
      this.addExistingOrPortedDid(payload);
    } else {
      // if verification data should be created
      this.loading.addDid = true;
      const VDpayload: FormData = this.verificationDataService.buildVerificationData
        (
          this.companyId,
          this.country,
          {
            type: this.verificationData.type,
            data: this.requirements,
            value: this.verificationData
          }
        );
      this.verificationDataService.createVerificationData(VDpayload).pipe(
        concatMap((res: VerificationData) => {
          const payload: AddDidRequest | AddDidManuallyRequest = {
            ...this.buildAddDidPayload(),
            verification_data_id: res.id
          };
          return this.getAddDidObservable(payload)
        })
      ).subscribe((res: string | DID) => {
        const value: string = this.planAddonHandling === 'manual' ? (<DID>res).number : <string>res;
        const message = this.translate.instant('company.dids.add-port-did.success.added', { value: value });
        this.onAddExistingOrPortedDid.emit(message);
        this.didsService.setAddPortDidFormValue(null);
        this.loading.addDid = false;
        this.close();
      }, () => {
        this.loading.addDid = false;
        this.close();
      });
    }
  }

  // submit when portability docs are required
  private onSubmitPortabilityDocs(): void {
    const payload: AddDidRequest | AddDidManuallyRequest = this.buildAddDidPayload();
    this.loading.addDid = true;
    this.getAddDidObservable(payload).pipe(
      concatMap((res: string | DID) => {
        const payload: string = this.planAddonHandling === 'manual' ? (<DID>res).number : <string>res;
        return this.didsService.getDid(payload)
      }),
      concatMap((res: DID) => {
        const payloadPortability: FormData = this.portabiltyService.buildPortabilityData(this.companyId, res, this.portabilityDocsForm.files);
        return this.portabiltyService.sendPortabilityRequest(payloadPortability)
      })
    ).subscribe((res: PortabilityRequestResponse) => {
      const message = this.translate.instant('company.dids.add-port-did.success.ported', { value: res.dids[0].number });
      this.onAddExistingOrPortedDid.emit(message);
      this.didsService.setAddPortDidFormValue(null);
      this.loading.addDid = false;
      this.close();
    }, () => {
      this.loading.addDid = false;
      this.close();
    });
  }

  private addExistingOrPortedDid(payload: AddDidRequest | AddDidManuallyRequest): void {
    this.loading.addDid = true;
    if(this.planAddonHandling === 'manual') {
      this.didsService.addDidManually(<AddDidManuallyRequest>payload).subscribe((res: DID) => {
        const message = this.translate.instant('company.dids.add-port-did.success.added', { value: res.number });
        this.onAddExistingOrPortedDid.emit(message);
        this.didsService.setAddPortDidFormValue(null);
        this.loading.addDid = false;
        this.close();
      }, () => {
        this.loading.addDid = false;
        this.close();
      });
    } else {
      this.didsService.addExistingOrPortedDid(this.companyId, <AddDidRequest>payload).subscribe((res: string) => {
        const message = this.translate.instant('company.dids.add-port-did.success.added', { value: res });
        this.onAddExistingOrPortedDid.emit(message);
        this.didsService.setAddPortDidFormValue(null);
        this.loading.addDid = false;
        this.close();
      }, () => {
        this.loading.addDid = false;
        this.close();
      });
    }
  }

  private buildAddDidPayload(): AddDidRequest | AddDidManuallyRequest {
    let payload: any = {
      class: this.didData.value.class,
      number: this.didData.value.phone,
      provider: this.didData.value.provider,
      type: this.didData.value.type,
      provider_reference: this.didData.value.providerReference,
      status: this.selectedStatus
    };
    if(this.planAddonHandling === 'manual') {
      payload = {
        ...payload,
        company_id: this.companyId
      }
    } else {
      payload = {
        ...payload,
        coupon_id: this.didData.value.couponId,
        country_code: this.country
      }
    }
    return payload;
  }

  private getAddDidObservable(payload: AddDidRequest | AddDidManuallyRequest): Observable<any> {
    return this.planAddonHandling === 'manual' ?
      this.didsService.addDidManually(<AddDidManuallyRequest>payload) :
      this.didsService.addExistingOrPortedDid(this.companyId, <AddDidRequest>payload)
  }

  public getDidDataSubmitToken(): string {
    let token: string;
    if(
      (this.selectedStatus === DID_STATUS.WAITING_VERIFICATION_DATA
      || this.selectedStatus === DID_STATUS.ACTIVE)
      && this.planAddonHandling === 'manual'
    ) {
      token = 'company.dids.add-port-did.popup.form.buttons.add';
    } else {
      token = 'company.dids.add-port-did.popup.form.buttons.next';
    }
    return token;
  }

  public close() {
    this.activeModal.close();
  }

  public ngOnDestroy() {
    if (this.languageSubscription) this.languageSubscription.unsubscribe();
  }
}
